import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaBoxOpen, FaSlidersH } from 'react-icons/fa';
import { toast } from 'react-toastify';

import axios from '../../services/axios';
import {
   getMovement,
   readMovementApiError,
   updateMovement,
} from '../../services/movements/movementsApi';
import {
   notifySnapshotBackfill,
   notifyStockWarnings,
} from '../../utils/movementFeedback';
import Loading from '../../components/Loading';
import StockImpactModal, {
   StockImpactHint,
} from '../../components/StockImpactModal';
import MovementConfirmModal from '../../components/MovementConfirmModal';
import MovementItemsEditor from '../../components/movements/MovementItemsEditor';
import MovementSummaryAside from '../../components/movements/MovementSummaryAside';
import {
   DOC_TYPE_OPTIONS_EDIT,
   INTEGER_RE,
   sumItemUnits,
} from '../../components/movements/constants';
import { useStockImpactCheck } from '../../hooks/useStockImpactCheck';
import { useUserPdvs } from '../../hooks/useUserPdvs';
import { moveTypeLabel, moveTypeVariant } from '../../utils/movementFormat';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import { Section as CardSection, SectionBody } from '../../styles/layout/Section';
import {
   ComposerPageGrid,
   Field,
   FieldHint,
   FieldInput,
   FieldLabel,
   FieldSelect,
   FieldTextarea,
   FormCard,
   FormCardTop,
   FormGrid,
   Section,
   SectionHead,
   SectionHeadRight,
   SectionIcon,
   SectionTitles,
} from '../../styles/ui/Composer';
import { GhostOnDarkButton } from '../../styles/ui/Button';
import { EmptyState } from '../../styles/feedback/EmptyState';
import { TextLink } from '../../styles/ui/Link';

// ===========================================================================
// Editor de movimento (handoff 0093 §3) — `PUT /movements/:id`.
//
// Header editável: pdv_id, move_type, doc_type, doc_number, descrição
// (`doc_key` nunca). Os itens são SUBSTITUIÇÃO INTEGRAL: o PUT envia o estado
// final da lista, não um diff. Movimento importado de XML é imutável
// (exclua e reimporte o corrigido) e movimento excluído/reverso não se edita
// — os dois casos têm guard de tela além do erro do backend.
//
// Redesenho 2026-09-12: mesma anatomia de composer das telas de criação
// (FC7 cap.2 §H.3), com o editor de itens e o cartão lateral compartilhados.
// Aqui o seletor de tipo de movimento CONTINUA existindo — corrigir um
// lançamento feito no tipo errado é justamente o que esta tela serve.
// ===========================================================================

export default function MovementEdit() {
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();

   const { pdvs, isLoading: loadingPdvs } = useUserPdvs();

   const [movement, setMovement] = useState(null);
   const [isLoading, setIsLoading] = useState(false);

   // header editável
   const [pdvId, setPdvId] = useState('');
   const [moveType, setMoveType] = useState('');
   const [docType, setDocType] = useState('');
   const [docNumber, setDocNumber] = useState('');
   const [description, setDescription] = useState('');

   const [productList, setProductList] = useState([]);

   // Modal unificado de confirmação do Salvar: null = fechado; aberto guarda
   // o achado FRESCO do warn guard de saldo.
   const [confirmState, setConfirmState] = useState(null);

   // Voltar preservando histórico (navegacao.md 1.6); fallback no detalhe.
   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate(`/movements/${id}`);
   };

   // Warn guard de saldo negativo (0093) — com `movementId`, o backend devolve
   // o efeito atual do movimento antes de calcular o impacto da lista nova.
   const stockImpact = useStockImpactCheck({
      movementId: id,
      pdvId,
      moveType,
      items: productList,
   });

   useEffect(() => {
      stockImpact.triggerCheck();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [productList, pdvId, moveType]);

   // Prefill: GET /movements/:id (shape cru, como o detalhe) + um
   // GET /products/:id por item pra hidratar SKU/nome (mesma técnica do
   // detalhe — N+1 conhecido do módulo).
   useEffect(() => {
      const fetchMovementData = async () => {
         setIsLoading(true);
         try {
            const movementData = await getMovement(id);
            setMovement(movementData);

            setPdvId(String(movementData?.pdv_id ?? ''));
            setMoveType(movementData?.move_type ?? '');
            setDocType(movementData?.doc_type ?? '');
            setDocNumber(
               movementData?.doc_number != null
                  ? String(movementData.doc_number)
                  : ''
            );
            setDescription(movementData?.movement_description ?? '');

            const items = movementData?.ItemMovements ?? [];
            if (items.length) {
               const productResponses = await Promise.all(
                  items.map((item) => axios.get(`/products/${item.prod_id}`))
               );
               const products = productResponses.map(
                  (res) => res.data.data?.product ?? res.data
               );
               setProductList(
                  items.map((item, idx) => ({
                     prod_id: Number(item.prod_id),
                     prod_qtd: Number(item.prod_qtd),
                     sku: String(
                        products[idx]?.sku ?? products[idx]?.prod_sku ?? ''
                     ),
                     prodName:
                        products[idx]?.name ?? products[idx]?.prod_name ?? '',
                  }))
               );
            } else {
               setProductList([]);
            }
         } catch (error) {
            setMovement(null);
            setProductList([]);
            toast.error(readMovementApiError(error).message);
         } finally {
            setIsLoading(false);
         }
      };

      fetchMovementData();
   }, [id]);

   const handleSubmitUpdate = async () => {
      try {
         setIsLoading(true);

         const { stockWarnings, snapshotBackfill, message } =
            await updateMovement(id, {
               movement: {
                  pdv_id: pdvId,
                  move_type: moveType,
                  doc_type: docType,
                  doc_number: docNumber,
                  movement_description: description,
               },
               items: productList,
            });

         toast.success(message);
         notifyStockWarnings(stockWarnings);
         notifySnapshotBackfill(snapshotBackfill);

         navigate(`/movements/${id}`);
      } catch (err) {
         const apiError = readMovementApiError(err);
         Object.entries(apiError.fieldErrors).forEach(([field, msg]) =>
            toast.error(`${field}: ${msg}`)
         );
         if (!Object.keys(apiError.fieldErrors).length) {
            toast.error(apiError.message);
         }
      } finally {
         setIsLoading(false);
      }
   };

   // Guarda do Salvar: consulta o impacto de saldo FRESCO (com movement_id) e
   // abre o modal unificado de confirmação — substitui o window.confirm.
   // Duplicidade não se aplica à edição.
   const handleConfirmUpdate = async () => {
      if (!pdvId || !moveType || !docType || productList.length === 0) {
         toast.error('Campos obrigatórios vazios.');
         return;
      }

      const negativeStock = await stockImpact.checkNow();
      setConfirmState({ negativeStock });
   };

   const handleConfirmModalConfirm = () => {
      setConfirmState(null);
      handleSubmitUpdate();
   };

   // Guards de estado: excluído/reverso não se edita; XML é imutável; e
   // movimento de transferência entre PDVs (handoff 0099 §4.3 — campo aditivo
   // `transfer` nos reads) pertence ao fluxo de transferência, que mantém o par
   // out/in e os acumuladores da ordem. A trava real é o 409
   // `transfer_movement_locked`; o guard de tela evita a viagem perdida.
   const isConfirmed = movement?.movement_flag === 'confirmed';
   const isXmlMovement = !!movement?.doc_key;
   const transferBinding = movement?.transfer ?? null;
   const isEditable =
      !!movement && isConfirmed && !isXmlMovement && !transferBinding;

   // PDV atual pode não estar no escopo do usuário — mantém visível no select
   // (o backend barra a escrita com 403 pdv_access_denied se for o caso).
   const pdvOptions =
      pdvId && !pdvs.some((pdv) => String(pdv.id) === String(pdvId))
         ? [
              ...pdvs,
              { id: pdvId, pdv_name: movement?.pdv_name ?? `PDV ${pdvId}` },
           ]
         : pdvs;

   // `doc_type` fora da lista oferecida continua selecionável — e marcado como
   // legado. Dois casos reais: tipo antigo que saiu do enum e TRANSFERÊNCIA,
   // que deixou de ser oferecida em 2026-09-12 (uso exclusivo do submódulo de
   // transferência). Preservar é obrigatório: salvar o movimento não pode
   // reescrever o tipo dele em silêncio. Converter OUTRO movimento para esses
   // valores, não — eles só aparecem para quem já os tem.
   const docTypeOptions = DOC_TYPE_OPTIONS_EDIT.some(
      (option) => option.value === docType
   )
      ? DOC_TYPE_OPTIONS_EDIT
      : [
           ...DOC_TYPE_OPTIONS_EDIT,
           { value: docType, label: `${docType} (legado)` },
        ].filter((option) => option.value);

   // Só é verdade no instante em que o campo está vazio COM O FOCO DENTRO: o
   // stepper normaliza para 1 no blur. Rede de segurança contra a corrida
   // "apaga a quantidade e clica em Salvar" (`Number('')` seria 0).
   const hasInvalidQty = productList.some(
      (item) =>
         String(item.prod_qtd ?? '').trim() === '' || Number(item.prod_qtd) < 1
   );

   const hasParams = !!pdvId && !!moveType && !!docType;
   const canSave = hasParams && productList.length > 0 && !hasInvalidQty;

   const pdvName = pdvOptions.find((pdv) => String(pdv.id) === String(pdvId))
      ?.pdv_name;
   const totalUnits = sumItemUnits(productList);

   const renderGuard = () => {
      if (!movement) {
         return (
            <EmptyState
               title="Movimento não encontrado"
               description="Verifique o ID e tente novamente."
            />
         );
      }
      if (isXmlMovement) {
         return (
            <EmptyState
               title="Movimento importado de XML"
               description="Movimentos importados de XML são imutáveis. Exclua o movimento e reimporte o XML corrigido."
            />
         );
      }
      if (transferBinding) {
         return (
            <EmptyState
               title="Movimento de transferência entre PDVs"
               description={`Este movimento pertence à transferência #${transferBinding.transfer_order_id} e só pode ser alterado pelo fluxo de transferência — expedição, conferência e cancelamento de remessa mantêm o par de movimentos e os saldos da ordem consistentes.`}
               action={
                  <TextLink
                     to={`/inventory/transfers/${transferBinding.transfer_order_id}`}
                  >
                     Abrir a transferência
                  </TextLink>
               }
            />
         );
      }
      return (
         <EmptyState
            title="Movimento não editável"
            description="Apenas movimentos confirmados podem ser editados."
         />
      );
   };

   return (
      <Container>
         <Loading isLoading={isLoading || loadingPdvs} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Editar movimento #{movement?.id ?? id}</PageTitle>
               <PageSubtitle>
                  Ajuste os dados do documento e a lista final de itens — o
                  saldo é recalculado pelo sistema.
               </PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               <GhostOnDarkButton type="button" onClick={handleBack}>
                  ← Voltar
               </GhostOnDarkButton>
            </PageHeaderActions>
         </PageHeader>

         {!isEditable && !isLoading ? (
            <CardSection>
               <SectionBody>{renderGuard()}</SectionBody>
            </CardSection>
         ) : (
            <>
               <ComposerPageGrid>
                  <FormCard>
                     <FormCardTop>
                        <h2>Correção do movimento #{movement?.id ?? id}</h2>
                        <p>
                           A lista de itens abaixo SUBSTITUI integralmente a
                           atual: o sistema devolve o efeito antigo ao estoque e
                           aplica o novo, na mesma transação.
                        </p>
                     </FormCardTop>

                     <Section>
                        <SectionHead>
                           <SectionIcon>
                              <FaSlidersH size={15} aria-hidden="true" />
                              <span className="step">1</span>
                           </SectionIcon>
                           <SectionTitles>
                              <div className="t">Parâmetros</div>
                              <div className="d">
                                 PDV, tipo e documento do movimento.
                              </div>
                           </SectionTitles>
                        </SectionHead>

                        <FormGrid>
                           <Field $span={4}>
                              <FieldLabel htmlFor="movement-edit-pdv">
                                 PDV<span className="req">*</span>
                              </FieldLabel>
                              <FieldSelect
                                 id="movement-edit-pdv"
                                 value={pdvId}
                                 onChange={(event) =>
                                    setPdvId(event.target.value)
                                 }
                                 disabled={isLoading || loadingPdvs}
                              >
                                 <option value="">Selecione um PDV</option>
                                 {pdvOptions.map((pdv) => (
                                    <option key={pdv.id} value={pdv.id}>
                                       {pdv.pdv_name}
                                    </option>
                                 ))}
                              </FieldSelect>
                           </Field>

                           <Field $span={4}>
                              <FieldLabel htmlFor="movement-edit-move-type">
                                 Tipo de movimento
                                 <span className="req">*</span>
                              </FieldLabel>
                              <FieldSelect
                                 id="movement-edit-move-type"
                                 value={moveType}
                                 onChange={(event) =>
                                    setMoveType(event.target.value)
                                 }
                                 disabled={isLoading}
                              >
                                 <option value="in">Entrada</option>
                                 <option value="out">Saída</option>
                              </FieldSelect>
                              <FieldHint>
                                 Trocar o tipo inverte o efeito no saldo.
                              </FieldHint>
                           </Field>

                           <Field $span={4}>
                              <FieldLabel htmlFor="movement-edit-doc-type">
                                 Documento<span className="req">*</span>
                              </FieldLabel>
                              <FieldSelect
                                 id="movement-edit-doc-type"
                                 value={docType}
                                 onChange={(event) =>
                                    setDocType(event.target.value)
                                 }
                                 disabled={isLoading}
                              >
                                 <option value="" disabled>
                                    Selecione o tipo de documento
                                 </option>
                                 {docTypeOptions.map((option) => (
                                    <option
                                       key={option.value}
                                       value={option.value}
                                    >
                                       {option.label}
                                    </option>
                                 ))}
                              </FieldSelect>
                           </Field>

                           <Field $span={4}>
                              <FieldLabel htmlFor="movement-edit-doc-number">
                                 Nº do documento
                              </FieldLabel>
                              <FieldInput
                                 id="movement-edit-doc-number"
                                 type="text"
                                 inputMode="numeric"
                                 value={docNumber}
                                 disabled={isLoading}
                                 onChange={(event) => {
                                    const { value } = event.target;
                                    if (value === '' || INTEGER_RE.test(value)) {
                                       setDocNumber(value);
                                    }
                                 }}
                              />
                           </Field>

                           <Field $span={12}>
                              <FieldLabel htmlFor="movement-edit-description">
                                 Descrição
                              </FieldLabel>
                              <FieldTextarea
                                 id="movement-edit-description"
                                 value={description}
                                 disabled={isLoading}
                                 onChange={(event) =>
                                    setDescription(
                                       event.target.value.toUpperCase()
                                    )
                                 }
                                 placeholder="Ex.: Correção de lançamento..."
                              />
                           </Field>
                        </FormGrid>
                     </Section>

                     <Section>
                        <SectionHead>
                           <SectionIcon>
                              <FaBoxOpen size={15} aria-hidden="true" />
                              <span className="step">2</span>
                           </SectionIcon>
                           <SectionTitles>
                              <div className="t">Itens (lista final)</div>
                              <div className="d">
                                 {productList.length
                                    ? `${productList.length} ${
                                         productList.length === 1
                                            ? 'item'
                                            : 'itens'
                                      } · ${totalUnits} un.`
                                    : 'O movimento precisa de ao menos um item.'}
                              </div>
                           </SectionTitles>
                           <SectionHeadRight>
                              <StockImpactHint
                                 status={stockImpact.status}
                                 count={stockImpact.negativeStock.length}
                                 onOpen={stockImpact.reopenModal}
                                 onRefresh={stockImpact.triggerCheck}
                              />
                           </SectionHeadRight>
                        </SectionHead>

                        <MovementItemsEditor
                           items={productList}
                           onChange={setProductList}
                           disabled={isLoading}
                           requirePdv={false}
                           pdvId={pdvId}
                           emptyTitle="Nenhum item na lista"
                           emptyDescription="A lista abaixo substitui integralmente a atual — o movimento precisa de ao menos um item."
                        />
                     </Section>
                  </FormCard>

                  <MovementSummaryAside
                     title="Resumo da correção"
                     typeLabel={moveTypeLabel(moveType)}
                     typeVariant={moveTypeVariant(moveType)}
                     rows={[
                        { label: 'Movimento', value: `#${movement?.id ?? id}` },
                        { label: 'PDV', value: pdvName },
                        {
                           label: 'Documento',
                           value: [docType, docNumber && `nº ${docNumber}`]
                              .filter(Boolean)
                              .join(' '),
                        },
                        { label: 'Itens', value: productList.length || '0' },
                        {
                           label: 'Quantidade total',
                           value: `${totalUnits} un.`,
                        },
                     ]}
                     /* Quantidade NÃO é item de checklist (2026-09-12): com a
                        lista vazia ficava verde por vacuidade. O stepper
                        normaliza no blur; o guard é rede de segurança. */
                     checklist={[
                        { label: 'PDV, tipo e documento', ok: hasParams },
                        {
                           label: 'Pelo menos um item na lista',
                           ok: productList.length > 0,
                        },
                     ]}
                     primaryLabel="Salvar alterações"
                     onPrimary={handleConfirmUpdate}
                     primaryDisabled={isLoading || !canSave}
                     primaryTitle={
                        hasInvalidQty
                           ? 'Há item com a quantidade em branco — conclua a edição para salvar'
                           : undefined
                     }
                     secondaryLabel="Cancelar"
                     onSecondary={handleBack}
                     secondaryDisabled={isLoading}
                     note="Movimento com data anterior a hoje reprocessa os snapshots de estoque em background."
                  />
               </ComposerPageGrid>

               <StockImpactModal
                  isOpen={stockImpact.isModalOpen}
                  negativeStock={stockImpact.negativeStock}
                  queryInfo={{
                     pdvName,
                     moveTypeLabel: moveTypeLabel(moveType),
                  }}
                  onClose={stockImpact.closeModal}
               />

               <MovementConfirmModal
                  isOpen={!!confirmState}
                  title="Confirmar alterações do movimento"
                  subtitle={[
                     pdvName,
                     moveTypeLabel(moveType),
                     docType,
                     docNumber ? `nº ${docNumber}` : '',
                  ]
                     .filter(Boolean)
                     .join(' · ')}
                  itemsCount={productList.length}
                  negativeStock={confirmState?.negativeStock ?? []}
                  confirmLabel="Salvar alterações"
                  confirmWithWarningsLabel="Salvar mesmo assim"
                  onCancel={() => setConfirmState(null)}
                  onConfirm={handleConfirmModalConfirm}
               />
            </>
         )}
      </Container>
   );
}
